<template>
  <div class="edit-lesson" v-loading="loading">
    <el-form :model="form" :rules="rules" ref="lessonForm" label-position="top">
      <el-form-item :label="$t('TITLE')" prop="name">
        <el-input
          v-model="form.name"
          :placeholder="$t('ENTER_TITLE')"
          maxlength="1024"
          show-word-limit
        />
      </el-form-item>

      <el-form-item :label="$t('CONTENT')" prop="content">
        <Tinymce
          v-model="form.content"
          :height="400"
          :key="lessonId || 'new'"
        />
      </el-form-item>

      <el-form-item :label="$t('RESOURCES')">
        <div class="resources-list">
          <div
            v-for="(resource, index) in form.resources"
            :key="index"
            class="resource-item"
          >
            <el-select
              v-model="resource.type"
              :placeholder="$t('SELECT_TYPE')"
              class="resource-type"
            >
              <!-- <el-option label="Image" value="image" /> -->
              <el-option label="Audio" value="audio" />
              <el-option label="Video" value="video" />
              <!-- <el-option label="Document" value="document" /> -->
            </el-select>

            <el-input
              v-model="resource.url"
              :placeholder="$t('ENTER_URL')"
              class="resource-url"
            />

            <el-button
              type="danger"
              icon="el-icon-delete"
              circle
              size="small"
              @click="removeResource(index)"
            />
          </div>

          <el-button
            type="primary"
            icon="el-icon-plus"
            @click="addResource"
            class="add-resource-btn"
          >
            {{ $t('ADD_RESOURCE') }}
          </el-button>
        </div>
      </el-form-item>
    </el-form>

    <div class="dialog-footer">
      <el-button @click="handleCancel">{{ $t('CANCEL') }}</el-button>
      <el-button type="primary" @click="handleSave" :loading="saving">
        {{ $t('SAVE') }}
      </el-button>
    </div>
  </div>
</template>

<script>
import examVaultService from '@/api/exam-vault'
import Tinymce from '../../../components/Tinymce'

export default {
  name: 'EditLesson',
  components: {
    Tinymce
  },
  props: {
    examVaultId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      loading: false,
      saving: false,
      lessonId: null,
      form: {
        name: '',
        content: '',
        resources: []
      },
      rules: {
        name: [
          {
            required: true,
            message: this.$t('TITLE_REQUIRED'),
            trigger: 'blur'
          },
          {
            max: 1024,
            message: this.$t('TITLE_MAX_LENGTH'),
            trigger: 'blur'
          }
        ],
        content: [
          {
            max: 10000,
            message: this.$t('CONTENT_MAX_LENGTH'),
            trigger: 'blur'
          }
        ]
      }
    }
  },

  created() {
    this.fetchLesson()
  },

  methods: {
    async fetchLesson() {
      this.loading = true
      try {
        const response = await examVaultService.getLessonByExamVaultId(
          this.examVaultId
        )
        if (response.data && response.data.lesson) {
          const lesson = response.data.lesson
          this.lessonId = lesson._id
          this.form = {
            name: lesson.name || '',
            content: lesson.content || '',
            resources: lesson.resources || []
          }
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          // Lesson doesn't exist yet, start with empty form
          console.log('No lesson found, creating new one')
        } else {
          this.$message.error(
            this.$t('FAILED_TO_LOAD_LESSON', 'Failed to load lesson')
          )
          console.error('Failed to fetch lesson:', error)
        }
      } finally {
        this.loading = false
      }
    },

    addResource() {
      this.form.resources.push({
        type: 'image',
        url: ''
      })
    },

    removeResource(index) {
      this.form.resources.splice(index, 1)
    },

    handleCancel() {
      this.$emit('close')
    },

    async handleSave() {
      try {
        await this.$refs.lessonForm.validate()
        this.saving = true

        const payload = {
          name: this.form.name,
          content: this.form.content,
          resources: this.form.resources.filter(
            (r) => r.type && r.url && r.url.trim() !== ''
          )
        }

        // Update will auto-create if lesson doesn't exist
        await examVaultService.updateLesson(this.examVaultId, payload)
        this.$message.success(
          this.$t('LESSON_SAVED_SUCCESS', 'Lesson saved successfully')
        )

        this.$emit('close')
      } catch (error) {
        if (error.message) {
          // Validation error
          console.log('Validation failed:', error)
        } else {
          this.$message.error(
            this.$t('FAILED_TO_SAVE_LESSON', 'Failed to save lesson')
          )
          console.error('Failed to save lesson:', error)
        }
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.edit-lesson {
  padding: 20px 0;

  .resources-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .resource-item {
    display: flex;
    gap: 10px;
    align-items: center;

    .resource-type {
      width: 150px;
      flex-shrink: 0;
    }

    .resource-url {
      flex: 1;
    }
  }

  .add-resource-btn {
    width: 100%;
    margin-top: 8px;
  }

  .dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid #eee;
  }

  @media (max-width: 768px) {
    .resource-item {
      flex-direction: column;
      align-items: stretch;

      .resource-type {
        width: 100%;
      }
    }
  }
}
</style>
